// The chips a reader has attached to the composer — tables, variables,
// dashboards picked from the side panel — as ONE strip in the composer's
// toolbar row, between the mic and whatever sits at the right-hand end. It
// scrolls sideways rather than wrapping, with a fade at the right edge when
// there is more to see, and the wheel scrolls it (Adam, 11 Sep). Shared by
// the session composer (AgentChat) and Platinum's start screen so the two
// cannot drift.
//
// Chips are not editable: remove one and add another. Filled, not tinted —
// a chip is a thing the reader attached and is about to send, so it reads
// as committed rather than as a faint hint.
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { INNER_RADIUS } from './surface.js'
import { Box, Chip, Tooltip } from '@mui/material'
import CancelIcon from '@mui/icons-material/Cancel'
import type { AgentComposerChip } from '../plugins.js'

export interface ComposerChipStripProps {
  chips: AgentComposerChip[]
  onRemove: (id: string) => void
  /** With no handler a chip is inert text. */
  onClick?: (id: string) => void
}

export default function ComposerChipStrip({ chips, onRemove, onClick }: ComposerChipStripProps) {
  // Does the strip overflow? Measured, so the fade appears only when there
  // really is more to see.
  const stripRef = useRef<HTMLDivElement>(null)
  const [overflow, setOverflow] = useState(false)
  useEffect(() => {
    const el = stripRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => setOverflow(el.scrollWidth > el.clientWidth + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [chips.length])

  const handleWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const el = stripRef.current
    if (!el || el.scrollWidth <= el.clientWidth) return
    // A vertical wheel over the strip scrolls it sideways; the chat area's own
    // wheel forwarding must not also scroll the list.
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
      el.scrollLeft += e.deltaY
      e.stopPropagation()
    }
  }, [])

  if (chips.length === 0) return null
  const clickable = Boolean(onClick)

  return (
    <Box
      ref={stripRef}
      data-testid="agent-composer-chips"
      data-overflow={overflow ? 'true' : 'false'}
      onWheel={handleWheel}
      sx={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        overflowX: 'auto',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        ...(overflow ? {
          maskImage: 'linear-gradient(to right, black calc(100% - 32px), transparent)',
          WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 32px), transparent)',
        } : {}),
      }}
    >
      {chips.map(chip => {
        const el = (
          <Chip
            key={chip.id}
            size="small"
            label={chip.label}
            onClick={clickable ? () => onClick!(chip.id) : undefined}
            onDelete={() => onRemove(chip.id)}
            deleteIcon={<span aria-label={`Remove ${chip.label}`} role="button" style={{ display: 'flex' }}><CancelIcon fontSize="small" /></span>}
            sx={{
              fontSize: 11,
              height: 22,
              flexShrink: 0,
              borderRadius: INNER_RADIUS,
              backgroundColor: '#1d4ed8',
              color: '#fff',
              fontWeight: 500,
              cursor: clickable ? 'pointer' : 'default',
              '&:hover': { backgroundColor: clickable ? '#1e40af' : '#1d4ed8' },
              // The delete icon inherits chip colour at low contrast against a
              // solid fill, so it is set explicitly.
              '& .MuiChip-deleteIcon': {
                color: 'rgba(255,255,255,0.75)',
                '&:hover': { color: '#fff' },
              },
            }}
          />
        )
        return chip.tooltip
          ? <Tooltip key={chip.id} title={chip.tooltip}><span style={{ flexShrink: 0 }}>{el}</span></Tooltip>
          : el
      })}
    </Box>
  )
}
